import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getVideoContext, VideoMeta } from "../services/video.service";

type Direction = "prev" | "next";

// ---- merge helpers (de-dupe & count how many new items were added) ---------
function mergeAppend(prev: VideoMeta[], items: VideoMeta[]) {
  if (items.length === 0) return { merged: prev, added: 0 };
  const seen = new Set(prev.map((v) => v.uid));
  const filtered: VideoMeta[] = [];
  for (const it of items) {
    if (!it?.uid || seen.has(it.uid)) continue;
    seen.add(it.uid);
    filtered.push(it);
  }
  return { merged: [...prev, ...filtered], added: filtered.length };
}

function mergePrepend(prev: VideoMeta[], items: VideoMeta[]) {
  if (items.length === 0) return { merged: prev, added: 0 };
  const seen = new Set(prev.map((v) => v.uid));
  const filtered: VideoMeta[] = [];
  for (const it of items) {
    if (!it?.uid || seen.has(it.uid)) continue;
    filtered.push(it);
  }
  return { merged: [...filtered, ...prev], added: filtered.length };
}

// ----------------------------------------------------------------------------

export function useVideoContextWindow(initialId?: string, tags: string[] = []) {
  const location = useLocation();

  const [videos, setVideos] = useState<VideoMeta[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [prevCursor, setPrevCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [initialSlide, setInitialSlide] = useState(0);
  const [swiperKey, setSwiperKey] = useState(0);

  const initSwiperDoneRef = useRef(false);
  const suppressEdgeLoadsRef = useRef(false);


  // Initial window
  useEffect(() => {
    if (!initialId) return;
    (async () => {
      try {
        const [prevCtx, nextCtx] = await Promise.all([
          getVideoContext(initialId, { tags, limit: 6, direction: "prev" }),
          getVideoContext(initialId, { tags, limit: 6, direction: "next" }),
        ]);

        // Reverse prev to keep chronological order
        const left = [...prevCtx.items].reverse();
        const center = nextCtx.current;
        const right = nextCtx.items;

        const combinedRaw = [...left, center, ...right].filter(Boolean) as VideoMeta[];
        const { merged: combined } = mergeAppend([], combinedRaw);

        const start = left.length;

        setVideos(combined);
        setCurrentIndex(start);
        setInitialSlide(start);

        setSwiperKey((k) => k + 1);
        initSwiperDoneRef.current = false;

        setPrevCursor(prevCtx.nextCursor);
        setNextCursor(nextCtx.nextCursor);
      } catch (e) {
        console.error("Initial video context load failed", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialId]);

  // Keep Swiper aligned if prepending
  useEffect(() => {
    if (!videos.length) return;
    suppressEdgeLoadsRef.current = true;
    const t = setTimeout(() => {
      suppressEdgeLoadsRef.current = false;
    }, 150);
    return () => clearTimeout(t);
  }, [videos.length, currentIndex]);

  const load = useCallback(
    async (direction: Direction) => {
      const cursor = direction === "next" ? nextCursor : prevCursor;
      if (suppressEdgeLoadsRef.current || !cursor || !videos.length) {
        return;
      }

      try {
        const anchor = direction === "next" ? videos[videos.length - 1].uid : videos[0].uid;
        const ctx = await getVideoContext(anchor, { tags, limit: 10, direction });

        if (direction === "next") {
          setVideos((prev) => {
            const { merged } = mergeAppend(prev, ctx.items);
            return merged;
          });
          setNextCursor(ctx.nextCursor);
        } else {
          // Reverse prev page to keep order
          const toAdd = [...ctx.items].reverse();
          suppressEdgeLoadsRef.current = true;

          setVideos((prev) => {
            const { merged, added } = mergePrepend(prev, toAdd);
            if (added > 0) setCurrentIndex((ix) => ix + added);
            return merged;
          });

          setPrevCursor(ctx.nextCursor);

          setTimeout(() => {
            suppressEdgeLoadsRef.current = false;
          }, 150);
        }
      } catch (e) {
        console.error(`Load ${direction} failed`, e);
      }
    },
    [nextCursor, prevCursor, videos, currentIndex, tags]
  );

  return {
    videos,
    currentIndex,
    setCurrentIndex,
    loadPrev: () => load("prev"),
    loadNext: () => load("next"),
    initialSlide,
    swiperKey,
    initSwiperDoneRef,
    suppressEdgeLoadsRef,
  };
}
